import React from "react";
import { useSeo } from "../useSeo";
import { VENUE } from "../data";

const VISIT_ITEMS = [
  { color: "var(--brand)", title: "Nosso momento, um convite pro café", text: "O café já começa às 9h — não precisa chegar mais cedo, é só vir pro café e ficar à vontade para a celebração das 10h.",
    icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></> },
  { color: "var(--blue)", title: "Vista-se como estiver confortável", text: "Não tem dress code — vem como você é.",
    icon: <><path d="M8 3.5 5 6v14a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6l-3-2.5" /><path d="M8 3.5a4 4 0 0 0 8 0" /></> },
  { color: "var(--green)", title: "Crianças e adolescentes são bem-vindos", text: "A equipe Kids & Teens estará cuidando e ensinando as crianças e dos adolescentes enquanto os pais estão no culto.",
    icon: <><circle cx="12" cy="8" r="3.2" /><path d="M5 20c.8-3.8 3.3-5.8 7-5.8s6.2 2 7 5.8" /></> },
  { color: "var(--purple)", title: "Onde estacionar e como chegar", text: "Endereço e rota completos logo abaixo, em \"Onde estamos\".",
    icon: <><circle cx="12" cy="9.5" r="2.4" /><path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21Z" /></> }
];

export default function Sobre() {
  useSeo({
    title: "Sobre a Mosaico | Igreja Batista Mosaico",
    description: "Nosso jeito de ser igreja, de onde vem o nome Mosaico, quem pastoreia e o que esperar na sua primeira visita à Igreja Batista Mosaico em Osasco.",
    path: "/sobre"
  });

  return (
    <>
      <div className="section-head" style={{ marginTop: 34 }}>
        <p className="eyebrow">Primeira vez aqui?</p>
        <h1>O que esperar na sua visita</h1>
        <p>As respostas rápidas de quem tá chegando — sem enrolação.</p>
      </div>
      <div className="grid grid-2" style={{ marginBottom: 36 }}>
        {VISIT_ITEMS.map((item) => (
          <div className="card visit-item" key={item.title}>
            <span className="icon" style={{ background: item.color }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{item.icon}</svg>
            </span>
            <div><h3>{item.title}</h3><p>{item.text}</p></div>
          </div>
        ))}
      </div>

      <div className="section-head">
        <p className="eyebrow">Sobre</p>
        <h2>Nosso jeito de ser igreja</h2>
        <p>Recebemos muita gente que já foi ferida por estruturas de hierarquia, cobrança de performance e distância entre quem lidera e quem senta no banco. Não é assim aqui.</p>
      </div>
      <div className="grid grid-3">
        <div className="card pillar">
          <span className="dot" style={{ background: "var(--orange)" }}></span>
          <h3>Comunhão, não hierarquia</h3>
          <p>Ninguém aqui é mais perto de Deus que você</p>
        </div>
        <div className="card pillar">
          <span className="dot" style={{ background: "var(--brand)" }}></span>
          <h3>Presença, não performance</h3>
          <p>Não precisa provar nada pra pertencer</p>
        </div>
        <div className="card pillar">
          <span className="dot" style={{ background: "var(--blue)" }}></span>
          <h3>Perto, não à distância</h3>
          <p>Pessoas que você esbarra no corredor, não só vê no palco</p>
        </div>
      </div>

      <div className="section-head">
        <p className="eyebrow">De onde vem o nome</p>
        <h2>Por que "Mosaico"</h2>
      </div>
      <div className="card story-card">
        <p>O nome "Mosaico" nasceu da certeza de que Deus faz arte com aquilo que o mundo considera perdido. Um mosaico é feito de peças quebradas, restos de algo que parecia não ter mais valor. Isoladas, essas peças não chamam atenção. São apenas fragmentos, marcados por rachaduras e histórias difíceis. Mas, quando se unem sob as mãos do Artista, viram uma obra de beleza única, cheia de cor, forma e sentido.</p>
        <p>A igreja é exatamente isso: gente de histórias partidas, cicatrizes visíveis, marcas profundas. Sozinhos, podemos até duvidar do nosso valor. Mas, juntos, nas mãos de Cristo, formamos uma resposta ao mundo:</p>
        <blockquote>"a beleza que nasce da graça, a comunhão que nasce da diversidade, o propósito que nasce da fragilidade compartilhada."</blockquote>
        <p>Ninguém é desperdiçado, ninguém é invisível. Cada pedaço importa. No Mosaico, celebramos não a perfeição, mas o encontro das diferenças, a reconstrução das vidas e a esperança de que Deus transforma o que foi quebrado em sinal do seu amor.</p>
        <p>Por isso, escolhemos esse nome. Porque queremos ser uma comunidade onde as peças não são descartadas, mas acolhidas. Um lugar onde as feridas são vistas, mas não são o fim da história — são o começo de algo novo, belo e cheio de significado. Aqui, cada pessoa encontra lugar na arte que Deus está criando.</p>
        <div className="verse-block">
          <p>"Já não somos estrangeiros nem forasteiros, mas concidadãos dos santos e membros da família de Deus. Somos edificados sobre o fundamento dos apóstolos e profetas, tendo Jesus Cristo como pedra principal. Nele, todo edifício é ajustado e cresce para tornar-se um templo santo no Senhor. E nele, vocês também estão sendo edificados juntos, para se tornarem morada de Deus por seu Espírito."</p>
          <cite>Efésios 2.19-22</cite>
        </div>
      </div>

      <div className="section-head">
        <h2>Quem pastoreia</h2>
      </div>
      <div className="card leader-row">
        <div className="leader-avatar">RM</div>
        <div>
          <div className="leader-name">Pr. Rodrigo Moraes Santos</div>
          <div className="leader-role">Em ministério desde 2006 · mestre em Ciência da Religião</div>
          <a className="maplink" href="https://www.rodrigomoraespastor.com.br/" target="_blank" rel="noopener">Site do pastor
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
          </a>
        </div>
      </div>

      <div className="section-head">
        <h2>Onde estamos</h2>
      </div>
      <div className="card venue-card">
        <img className="venue-photo" src={VENUE.photoSobre} alt="Entrada da Igreja Batista Mosaico, em Osasco" />
        <div className="addr-row">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.4" /></svg>
          <div>
            <div className="addr-text">{VENUE.address}</div>
            <a className="maplink" href={VENUE.mapsUrl} target="_blank" rel="noopener">Ver rota no mapa
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
            </a>
          </div>
        </div>
        <div className="venue-caption">Nossa fachada, na Av. dos Autonomistas.</div>
      </div>
    </>
  );
}
